import { LinkIcon } from '@/assets/icons'
import { AttachmentIndicator, AttachmentsViewerModal } from '@components/Attachments'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Idea } from '../types'
import {
	CardContainer,
	CardDescription,
	CardHeader,
	CardLinks,
	CardLocation,
	CardTags,
	CardTitle,
	LinkInfo,
	LinkPreviewCard,
	LinkSource,
	LinkThumbnail,
	LinkUrl,
	LocationPin,
	Tag,
} from './IdeaCard.styles'

interface IdeaCardProps {
	idea: Idea
	onEdit: (ideaId: string) => void
}

const IdeaCard: React.FC<IdeaCardProps> = ({ idea, onEdit }) => {
	const { tripId } = useParams<{ tripId: string }>()
	const [isViewerOpen, setIsViewerOpen] = useState(false)

	const handleCardClick = () => {
		onEdit(idea.id.toString())
	}

	return (
		<>
		<CardContainer onClick={handleCardClick}>
			<CardHeader>
				<CardTitle>{idea.name}</CardTitle>
			</CardHeader>

			{idea.description && <CardDescription>{idea.description}</CardDescription>}

			{idea.location?.primaryText && (
				<CardLocation>
					<LocationPin
						width={24}
						height={24}
						fill="currentColor"
					/>
					{idea.location.primaryText}
				</CardLocation>
			)}

		{idea.links && idea.links.length > 0 && (
			<CardLinks onClick={e => e.stopPropagation()}>
				{idea.links.map(link => {
						// If it starts with http:// or https://, use as-is
						// Otherwise, if it looks like a URL (contains dots and no spaces), prepend https://
						const hasProtocol = link.url.startsWith('http://') || link.url.startsWith('https://')
						const looksLikeUrl = link.url.includes('.') && !link.url.includes(' ')

						let hrefUrl = link.url
						if (!hasProtocol && looksLikeUrl) {
							hrefUrl = `https://${link.url}`
						}

						const isClickable = hasProtocol || looksLikeUrl

						// Get source-specific icon color
						const getSourceColor = (source: string) => {
							switch (source.toLowerCase()) {
								case 'tiktok':
									return '#000000'
								case 'instagram':
									return '#E4405F'
								case 'youtube':
									return '#FF0000'
								case 'googlemaps':
									return '#4285F4'
								case 'applemaps':
									return '#1d1d1f'
								case 'website':
									return '#6b7280'
								default:
									return '#6b7280'
							}
						}

						// Format source name for display
						const formatSourceName = (source: string) => {
							if (source === 'GoogleMaps') return 'Google Maps'
							if (source === 'AppleMaps') return 'Apple Maps'
							return source
						}

						return (
							<LinkPreviewCard
								key={link.id}
								href={isClickable ? hrefUrl : undefined}
								target={isClickable ? '_blank' : undefined}
								rel={isClickable ? 'noopener noreferrer' : undefined}
								as={isClickable ? 'a' : 'div'}
								$clickable={isClickable}
							>
								<LinkThumbnail src={link.thumbnail || undefined}>
									{!link.thumbnail && <LinkIcon width={24} height={24} fill="#9ca3af" />}
								</LinkThumbnail>
								<LinkInfo>
									<LinkSource $color={getSourceColor(link.source)}>
										{formatSourceName(link.source)}
									</LinkSource>
									<LinkUrl>{link.url}</LinkUrl>
								</LinkInfo>
							</LinkPreviewCard>
						)
					})}
				</CardLinks>
			)}

			{idea.tags && idea.tags.length > 0 && (
				<CardTags>
					{idea.tags.map(tag => (
						<Tag key={tag}>{tag}</Tag>
					))}
				</CardTags>
			)}

			{idea.fileIds && idea.fileIds.length > 0 && (
				<AttachmentIndicator
					count={idea.fileIds.length}
					onClick={e => {
						e.stopPropagation()
						setIsViewerOpen(true)
					}}
				/>
			)}
		</CardContainer>

		{tripId && (
			<AttachmentsViewerModal
				isOpen={isViewerOpen}
				onClose={() => setIsViewerOpen(false)}
				tripId={tripId}
				fileIds={idea.fileIds || []}
				title={`${idea.name} — Attachments`}
				onEditClick={() => {
					setIsViewerOpen(false)
					onEdit(idea.id.toString())
				}}
			/>
		)}
		</>
	)
}

export default IdeaCard
